import { ref } from 'vue'
import useAuthentication from '~/composables/useAuthentication'
import { FORBIDDEN } from '~/constants/http'
import { SUBSCRIPTION_PRICE_ASSESSMENTS_ALL_PRICES } from '~/constants/services/constants'
import { useMembershipsStore } from '~/stores/memberships'
import { asyncSome } from '~/utils/array'
import { hasPricingMenu } from '~/utils/memberships'
import { logError } from '~/utils/log-error'

export const useAuthorization = () => {
  const router = useRouter()
  const membershipsStore = useMembershipsStore()
  const { isAuthenticated } = useAuthentication()
  const { $membershipApi } = useNuxtApp()
  const isAuthorized = ref(false)

  const hasMembership = async (membershipName: string) => {
    try {
      const authenticated = isAuthenticated()
      if (!authenticated) return false

      let isMembershipActive =
        membershipsStore.isMembershipActive(membershipName)

      let memberships = []
      if (authenticated && !isMembershipActive) {
        memberships = await $membershipApi.getActiveMemberships()
        isMembershipActive = memberships.includes(membershipName)
      }
      if (
        !isMembershipActive &&
        membershipName === SUBSCRIPTION_PRICE_ASSESSMENTS_ALL_PRICES
      ) {
        isMembershipActive = hasPricingMenu(memberships)
      }
      return isMembershipActive
    } catch (error) {
      logError(error)
      return false
    }
  }

  const hasMemberships = async (membershipNames: string[]) => {
    try {
      const authenticated = isAuthenticated()
      if (!authenticated) return false
      const memberships = await $membershipApi.getActiveMemberships(false)
      const isMembershipsValue = memberships.some((membership) =>
        membershipNames.includes(membership),
      )

      return isMembershipsValue && authenticated
    } catch (error) {
      logError(error)
      return false
    }
  }

  const hasAuthorized = async (
    subscription: string,
    currentSubscription?: string,
  ) => {
    try {
      const authenticated = isAuthenticated()
      if (!authenticated) return false
      const authorized = await hasMembership(
        subscription ?? currentSubscription,
      )
      return authorized && authenticated
    } catch (error) {
      logError(error)
      return false
    }
  }

  const hasOneOfThisMemberships = async (...memberships) => {
    try {
      return await asyncSome(memberships, hasMembership)
    } catch (error) {
      logError(error)
      return false
    }
  }

  const updateMembershipsAndRedirect = async (error, route) => {
    if (error?.status === FORBIDDEN || error.data?.status === FORBIDDEN) {
      try {
        await $membershipApi.getActiveMemberships()
        await router.push(route)
      } catch (error) {
        logError(error)
        await router.push(route)
      }
    }
  }

  return {
    isAuthorized,
    hasAuthorized,
    hasOneOfThisMemberships,
    hasMembership,
    hasMemberships,
    updateMembershipsAndRedirect,
  }
}
